import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import FeedbackInbox from './components/FeedbackInbox';
import { apiService } from './utils/api';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';

function App() {
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const isFeedbackRoute = window.location.pathname === '/feedback';

  // Check API health on startup
  useEffect(() => {
    const checkHealth = async () => {
      try {
        await apiService.healthCheck();
        setIsHealthy(true);
        setHealthError(null);
      } catch (error: any) {
        setIsHealthy(false);
        setHealthError(error.response?.data?.detail || error.message || 'API connection failed');
      }
    };

    checkHealth();
  }, []);

  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  const handleSettingsClick = () => {
    // Placeholder for settings
    alert('Settings coming soon!');
  };

  // Loading state
  if (isHealthy === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-megi-red" />
          <p className="text-gray-600">Connecting to MEGI...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!isHealthy) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onMenuClick={handleMenuClick} onSettingsClick={handleSettingsClick} />
        
        <div className="max-w-2xl mx-auto p-6 mt-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
              <h2 className="text-lg font-bold text-red-800">Connection Error</h2>
            </div>
            
            <p className="text-red-700 mb-4">
              Unable to connect to the MEGI backend API.
            </p>
            
            <div className="bg-red-100 rounded p-3 mb-4">
              <p className="text-sm text-red-600 font-mono">{healthError}</p>
            </div>
            
            <div className="text-sm text-red-600 space-y-2">
              <p><strong>Troubleshooting:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Make sure the backend server is running on port 8000</li>
                <li>Run: <code className="bg-red-200 px-1 rounded">cd backend && python -m uvicorn app.main:app --reload</code></li>
                <li>Or use Docker: <code className="bg-red-200 px-1 rounded">docker-compose up</code></li>
                <li>Check if AI provider credentials are configured (optional for demo)</li>
              </ul>
            </div>
            
            <button
              onClick={() => window.location.reload()}
              className="mt-4 btn-primary"
            >
              Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main app
  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuClick={handleMenuClick} onSettingsClick={handleSettingsClick} />
      
      {/* Success indicator */}
      <div className="bg-green-50 border-b border-green-200 p-2">
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
          <p className="text-sm text-green-700">
            Connected to MEGI API
          </p>
        </div>
      </div>

      {/* Sidebar (placeholder) */}
      {showMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowMenu(false)}>
          <div className="fixed left-0 top-0 h-full w-64 bg-white shadow-lg z-50">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Menu</h2>
              <nav className="space-y-2">
                <button
                  onClick={() => {
                    window.location.href = '/';
                  }}
                  className="block w-full text-left p-2 rounded bg-gray-100 text-gray-900"
                >
                  Dashboard
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {isFeedbackRoute ? <FeedbackInbox /> : <Dashboard userId="default" />}
    </div>
  );
}

export default App;
